import React, { FC, ReactNode, useState } from 'react';
import { Grid, Tooltip, Typography } from '@material-ui/core';
import { lighten, makeStyles, useTheme } from '@material-ui/core/styles';

import { useCurrentUser } from 'hooks';
import { UserAvatar } from 'components';

interface Props {
  scale: number;
  valueNode: ReactNode;
  percentage: string;
}

const useStyles = makeStyles(theme => ({
  rank: {
    position: 'relative',
  },
  arrow: {
    position: 'absolute',
    top: -22,
    height: 0,
    width: 0,
    borderLeft: `${10}px solid transparent`,
    borderRight: `${10}px solid transparent`,
    borderTop: `${20}px solid transparent`,
  },
  bar: {
    borderRadius: theme.spacing(1),
    height: 15,
  },
}));

const RankChart: FC<Props> = ({ scale, valueNode, percentage }) => {
  const currentUser = useCurrentUser();
  const theme = useTheme();
  const classes = useStyles();
  const [hovered, setHovered] = useState(false);

  const { secondary, warning, error } = theme.palette;
  const tercile = getTercile();
  const arrowColor = getColor();

  function getTercile() {
    if (scale > 0 && scale <= 0.33) {
      return 'low';
    }

    if (scale > 0.33 && scale <= 0.66) {
      return 'mid';
    }

    if (scale > 0.66) {
      return 'high';
    }

    return '';
  }

  function getColor() {
    if (tercile === 'low') {
      return error.main;
    }

    if (tercile === 'mid') {
      return warning.main;
    }

    if (tercile === 'high') {
      return secondary.main;
    }

    return '';
  }

  function renderGradient() {
    const red = `linear-gradient(90deg, ${error.dark} 0%, ${lighten(error.dark, 0.7)} 100%)`;
    const orange = `linear-gradient(90deg, ${warning.dark} 0%, ${lighten(warning.dark, 0.7)} 100%)`;
    const green = `linear-gradient(90deg, ${lighten(secondary.dark, 0.7)} 0%, ${lighten(
      secondary.dark,
      0.3
    )} 100%)`;

    switch (tercile) {
      case 'low':
        return <div style={{ background: red }} className={classes.bar} />;
      case 'mid':
        return <div style={{ background: orange }} className={classes.bar} />;
      case 'high':
      default:
        return <div style={{ background: green }} className={classes.bar} />;
    }
  }

  function renderTooltip() {
    return (
      <Grid container alignItems="center" spacing={1} wrap="nowrap">
        <Grid item>
          <UserAvatar user={currentUser} size={24} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Typography noWrap display="inline" variant="overline">
            {currentUser.full_name}
          </Typography>
        </Grid>
        <Grid item>&mdash;</Grid>
        <Grid item>
          <Typography display="inline" variant="caption">
            {valueNode}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <div
      className={classes.rank}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Tooltip open={hovered} placement="bottom" title={renderTooltip()}>
        <div
          style={{
            left: percentage,
            transform: `translateX(-${percentage})`,
            borderTopColor: arrowColor,
          }}
          className={classes.arrow}
        />
      </Tooltip>
      {renderGradient()}
    </div>
  );
};

export default RankChart;
